import { type PayloadAction, createSlice } from "@reduxjs/toolkit"

import {
  SCROLL_PAGE_SIZE,
  afterFailedFetch,
  afterFetch,
  beforeFetch
} from "../../../redux/shared/paged-list"
import { getResponseMessage } from "../../../utils/handlers/get-response-message"
import { logout } from "../../LoginPage/redux/auth.thunks"
import { suppliersSliceName } from "./suppliers.constants"
import {
  createSupplier,
  deleteSupplier,
  fetchSupplierDetails,
  fetchSuppliers,
  updateSupplier
} from "./suppliers.thunks"
import type { SuppliersFilters, SuppliersState } from "./suppliers.types"

const initialState: SuppliersState = {
  fetching: false,
  requestId: null,
  result: {
    count: 0,
    next: null,
    results: []
  },
  filters: {
    page: 1,
    page_size: SCROLL_PAGE_SIZE,
    q: null,
    ordering: null
  },
  saveFetching: false,
  saveResult: null,
  supplierDetailsFetching: false,
  supplierDetails: null
}

const suppliersSlice = createSlice({
  name: suppliersSliceName,
  initialState,
  reducers: {
    suppliersSetFilters: (
      state,
      action: PayloadAction<Partial<SuppliersFilters>>
    ) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    suppliersClearFilters: (state) => {
      state.filters = initialState.filters
    },
    suppliersClearSaveResult: (state) => {
      state.saveResult = null
    },
    suppliersClearDetails: (state) => {
      state.supplierDetails = null
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSuppliers.pending, (state, action) => {
        state.fetching = true
        state.requestId = action.meta.requestId
        state.result = beforeFetch(
          state.result,
          initialState.result,
          action.meta?.arg?.isMore
        )
      })
      .addCase(fetchSuppliers.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.fetching = false
        state.requestId = null
        state.result = afterFetch(
          state.result,
          action.payload,
          action.meta?.arg?.isMore
        )
      })
      .addCase(fetchSuppliers.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.fetching = false
        state.requestId = null
        state.result = afterFailedFetch(
          state.result,
          initialState.result,
          action.meta?.arg?.isMore
        )
      })
      .addCase(createSupplier.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(createSupplier.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = {
          type: "supplier_create",
          message: getResponseMessage(
            action.payload,
            "Supplier created successfully."
          )
        }
      })
      .addCase(createSupplier.rejected, (state) => {
        state.saveFetching = false
      })
      .addCase(updateSupplier.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(updateSupplier.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = {
          type: "supplier_update",
          message: getResponseMessage(
            action.payload,
            "Supplier updated successfully."
          )
        }
      })
      .addCase(updateSupplier.rejected, (state) => {
        state.saveFetching = false
      })
      .addCase(deleteSupplier.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(deleteSupplier.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = {
          type: "supplier_delete",
          message: getResponseMessage(
            action.payload,
            "Supplier deleted successfully."
          )
        }
        state.result = {
          ...state.result,
          count: Math.max(0, state.result.count - 1),
          results: state.result.results.filter(
            (supplier) => supplier.id !== action.payload?.id
          )
        }
      })
      .addCase(deleteSupplier.rejected, (state) => {
        state.saveFetching = false
      })
      .addCase(fetchSupplierDetails.pending, (state) => {
        state.supplierDetailsFetching = true
      })
      .addCase(fetchSupplierDetails.fulfilled, (state, action) => {
        state.supplierDetailsFetching = false
        state.supplierDetails = action.payload
      })
      .addCase(fetchSupplierDetails.rejected, (state) => {
        state.supplierDetailsFetching = false
      })
      .addCase(logout.fulfilled, () => initialState)
  }
})

export const {
  suppliersSetFilters,
  suppliersClearFilters,
  suppliersClearSaveResult,
  suppliersClearDetails
} = suppliersSlice.actions

export default suppliersSlice
